import type { ReactNode } from 'react';
import { Box, paletteColors, Stack, Typography } from '@evaneos/b2b-ui';
import ForumOutlinedIcon from '@evaneos/b2b-ui/icons/ForumOutlined';
import RouteOutlinedIcon from '@evaneos/b2b-ui/icons/RouteOutlined';
import PublicOutlinedIcon from '@evaneos/b2b-ui/icons/PublicOutlined';
import LocalAtmOutlinedIcon from '@evaneos/b2b-ui/icons/LocalAtmOutlined';
import AssignmentOutlinedIcon from '@evaneos/b2b-ui/icons/AssignmentOutlined';

const ICON_SX = { color: paletteColors.green[700], fontSize: '2.4rem' };

const TOOLS: { icon: ReactNode; title: string; description: string }[] = [
    {
        icon: <ForumOutlinedIcon sx={ICON_SX} />,
        title: 'Request Manager',
        description:
            'Manage traveler requests, handle messaging, phone and video calls, and schedule appointments with Google Calendar integration.',
    },
    {
        icon: <RouteOutlinedIcon sx={ICON_SX} />,
        title: 'Trip Planner',
        description:
            'Build tailor-made travel quotes and compelling proposals for your clients in a streamlined editor.',
    },
    {
        icon: <PublicOutlinedIcon sx={ICON_SX} />,
        title: 'Offer Manager',
        description:
            "Publish your agency's curated itineraries directly on Evaneos.com and reach millions of traveler prospects.",
    },
    {
        icon: <LocalAtmOutlinedIcon sx={ICON_SX} />,
        title: 'Cash Manager',
        description:
            'Handle international, multi-currency transactions with ease — collect payments and manage agency cash flow.',
    },
    {
        icon: <AssignmentOutlinedIcon sx={ICON_SX} />,
        title: 'Agency Insight',
        description:
            'Track your agency performance with real-time analytics: conversion rates, response times, and booking metrics.',
    },
];

const ToolCard = ({
    icon,
    title,
    description,
}: {
    icon: ReactNode;
    title: string;
    description: string;
}) => (
    <Box
        sx={{
            bgcolor: paletteColors.neutral[0],
            borderRadius: 2,
            border: `1px solid ${paletteColors.neutral[200]}`,
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            transition: 'box-shadow 0.2s',
            '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.08)' },
        }}
    >
        <Box
            sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: paletteColors.green[100],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {icon}
        </Box>
        <Typography
            component="h3"
            variant="subtitle1"
            fontWeight={700}
            sx={{ color: paletteColors.neutral[900] }}
        >
            {title}
        </Typography>
        <Typography variant="body2" sx={{ color: paletteColors.neutral[600], lineHeight: 1.6 }}>
            {description}
        </Typography>
    </Box>
);

export const TravelSuiteTools = () => (
    <Box
        component="section"
        sx={{
            backgroundColor: paletteColors.neutral[0],
            px: { xs: 2, md: 8 },
            py: { xs: 8, md: 12 },
        }}
    >
        <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
            <Stack spacing={2} mb={6} alignItems={{ xs: 'flex-start', md: 'center' }}>
                <Typography
                    variant="titleMoretXL"
                    component="h2"
                    sx={{ color: paletteColors.neutral[900], textAlign: { md: 'center' } }}
                >
                    5 essential tools for partner agencies
                </Typography>
                <Typography
                    variant="body1"
                    sx={{
                        color: paletteColors.neutral[600],
                        fontSize: '1.8rem',
                        textAlign: { md: 'center' },
                        maxWidth: '700px',
                    }}
                >
                    Everything you need to manage your business with Evaneos, from first contact to
                    final invoice.
                </Typography>
            </Stack>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                    gap: 3,
                }}
            >
                {TOOLS.map((tool) => (
                    <ToolCard key={tool.title} {...tool} />
                ))}
            </Box>
        </Box>
    </Box>
);
